import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { MbsSize } from 'mbs-ui-kit/utils/enums/mbs-size-enum';
import { ClonePlanModalComponent } from 'mbs-ui-shared@components/plans-modal/clone-plan-modal/clone-plan-modal.component';
import { DeletePlanModalComponent } from 'mbs-ui-shared@components/plans-modal/delete-plan-modal/delete-plan-modal.component';
import { DeletePlanWithDataModalComponent } from 'mbs-ui-shared@components/plans-modal/delete-plan-with-data-modal/delete-plan-with-data-modal.component';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { ComputerModalsFacade } from 'mbs-ui-shared@facades/computer.modals.facade';
import Computer, { AgentType, OsType } from 'mbs-ui-shared@models/Computer';
import { OldPagePlanTypes, PlanFormatTypes, PlanMode, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { AvailablePlanStartModes } from 'mbs-ui-shared@models/backup/available-plan-start-modes';
import { PlanInfo } from 'mbs-ui-shared@models/backup/plan-info-model';
import { RecoveryInfo } from 'mbs-ui-shared@models/backup/recovery-info.interface';
import { RecoveryStatus } from 'mbs-ui-shared@models/backup/recovery-status.enum';
import { baseDateFormatWithSeconds } from 'mbs-ui-shared@utils/date';
import { getWizardComponentType } from 'mbs-ui-shared@utils/getWizardComponentType';
import moment from 'moment';
import { catchError, noop, Observable, of } from 'rxjs';
import { CreateRVPlanParams, PlansWizardMode, RestoreWizardSettings } from 'wizards/models/base/base-plan-models';
import { RemoteManagementWizardsService, WizardSettings } from 'wizards/services/remote-management-wizards.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class PlanModalWrapper {
  private facade = inject(ComputerBackupFacade);
  i18nPipe = inject(I18NextPipe);
  private router = inject(Router);
  private wizardService = inject(RemoteManagementWizardsService);
  private modals = inject(ComputerModalsFacade);

  startGenericRestoreWizard(computer?: Computer): void {
    this.wizardService.startGenericRestoreWizard(computer);
  }

  public createRestoreVerificationPlan(params: CreateRVPlanParams): Observable<boolean> {
    return this.wizardService.createRestoreVerificationPlan(params);
  }

  startRestoreBySettings({
    computer,
    isNBF,
    type,
    initialFormData,
    sessionId,
    sourceHid,
    sourceUserId,
    targetComputerData
  }: RestoreWizardSettings): Observable<any> {
    const wizardComponent = getWizardComponentType(type);

    const newParams: WizardSettings = {
      hid: computer.hid,
      mode: PlanMode.create,
      wizardMode: PlansWizardMode.forRMM,
      planId: '',
      isNBF: isNBF,
      isOffline: !computer.online,
      type: type,
      isUnix: Computer.isUnix(computer),
      isLinux: Computer.isLinux(computer),
      isMac: Computer.isMac(computer),
      compName: computer.name,
      compFullName: Computer.getComputerName(computer),
      compVmName: Computer.getComputerVmName(computer),
      email: computer?.userAccount?.accountName || null,
      backupVersion: Computer.getAgent(computer, AgentType.Backup)?.version || null,
      userId: computer?.userAccount?.id || null,
      sourceHid,
      sourceUserId,
      sessionId,
      initialFormData,
      targetComputerData
    } as WizardSettings;

    return this.wizardService.setWizard(wizardComponent, { size: MbsSize.lg }, newParams).afterClosed;
  }

  handleEditPlan(plan: PlanInfo, computer: Computer): void {
    if (plan.type === PlanTypes.CloudVMBackupEC2Plan) {
      this.openEC2Plan(computer, plan);
      return;
    }

    const isVirtual = plan.type === PlanTypes.BackupHyperV || plan.type === PlanTypes.BackupVMware;
    if (isVirtual && plan.formatType === PlanFormatTypes.NBF) {
      if (plan.isClusterBackupEnabled && !Computer.IsSupportedAgentVersion(computer, AgentType.Backup, 790, true)) {
        this.wizardService.openErrorVersionModal(true);
        return;
      }
      if (plan.isExcludedFilesPresent && !Computer.IsSupportedAgentVersion(computer, AgentType.Backup, 783)) {
        this.wizardService.openErrorVersionModal(true, this.i18nPipe.transform('wizards:virtual_wizards_error_fields_exclude_text_alert'));
        return;
      }
    }

    const planTypeRelatedComponent = getWizardComponentType(plan.type);

    this.actionAndReloadPlans(
      computer,
      this.wizardService.setWizard(
        planTypeRelatedComponent,
        {
          size: MbsSize.lg
        },
        this.getWizardSettings(plan?.type, computer, plan, true, plan?.formatType === PlanFormatTypes.NBF)
      ).afterClosed
    );
  }

  handleCreatePlan(type: PlanTypes, computer: Computer, isNBF = false): void {
    if (type === PlanTypes.CloudVMBackupEC2Plan) {
      this.openEC2Plan(computer, { type } as PlanInfo);
      return;
    }

    const planTypeRelatedComponent = getWizardComponentType(type);
    this.actionAndReloadPlans(
      computer,
      this.wizardService.setWizard(
        planTypeRelatedComponent,
        {
          size: MbsSize.lg
        },
        this.getWizardSettings(type, computer, null, false, isNBF)
      ).afterClosed
    );
  }

  handleDeletePlan(plan: PlanInfo, computer: Computer, withData = false): void {
    const modal = withData ? DeletePlanWithDataModalComponent : DeletePlanModalComponent;

    this.modals.openPlanBasedModal(modal, computer, plan);
  }

  handleClonePlan(plan: PlanInfo, computer: Computer): void {
    this.modals.openPlanBasedModal(ClonePlanModalComponent, computer, plan);
  }

  handleStartPlan(plan: PlanInfo, computer: Computer, mode: AvailablePlanStartModes = AvailablePlanStartModes.Regular): void {
    if (!plan.lastRunInfo) {
      plan.lastRunInfo = { status: RecoveryStatus.Started } as RecoveryInfo;
    } else plan.lastRunInfo.status = RecoveryStatus.Started;
    this.actionAndReloadPlans(
      computer,
      this.facade.startPlan(computer, plan.id, mode).pipe(
        catchError((e) => {
          plan.lastRunInfo.status = RecoveryStatus.Failed;
          return e;
        })
      )
    );
  }

  handleStopPlan(plan: PlanInfo, computer: Computer): void {
    this.actionAndReloadPlans(
      computer,
      this.facade.stopPlan(computer, plan.id).pipe(
        catchError((e) => {
          if (plan.lastRunInfo) plan.lastRunInfo.status = RecoveryStatus.Failed;
          return e;
        })
      )
    );
  }

  public checkVersionAndTypeForOldPage(type: PlanTypes, computer: Computer, isCBF = false): boolean {
    const version = computer.os === OsType.windows ? 461 : 170;

    return (
      !!OldPagePlanTypes[type] ||
      (type === PlanTypes.DataBasePlan && !Computer.IsSupportedAgentVersion(computer, AgentType.Backup, version))
    );
  }

  public getWizardSettings(type: PlanTypes, computer: Computer, plan: PlanInfo = null, edit = false, isNBF = false): WizardSettings {
    const newParams: WizardSettings = {
      hid: computer.hid,
      mode: edit ? PlanMode.edit : PlanMode.create,
      wizardMode: PlansWizardMode.forRMM,
      planId: plan?.id || '',
      isNBF: isNBF || type === PlanTypes.BackupProxmox || type === PlanTypes.RestoreProxmox,
      isOffline: !computer.online,
      type: type,
      isNetBuild: Computer.isNetBuild(computer),
      isUnix: Computer.isUnix(computer),
      isLinux: Computer.isLinux(computer),
      isMac: Computer.isMac(computer),
      compName: computer.name,
      compFullName: Computer.getComputerName(computer),
      compVmName: Computer.getComputerVmName(computer),
      email: computer?.userAccount?.accountName || null,
      backupVersion: Computer.getAgent(computer, AgentType.Backup)?.version || null,
      userId: computer?.userAccount?.id || null
    } as WizardSettings;

    if (edit) newParams.wasNotExecuted = !plan?.lastRunInfo?.status;

    return newParams;
  }

  private actionAndReloadPlans(computer: Computer, action: Observable<any>) {
    action.subscribe(() => this.facade.loadPlans({ computer, force: true, quiet: true }));
  }

  private openEC2Plan(computer: Computer, plan: PlanInfo): void {
    this.modals
      .openEC2PlanModal()
      .pipe(catchError(() => of(false)))
      .subscribe((confirm) => {
        if (confirm) {
          this.setParamsForOldWizardsToLocalStorage(computer, plan);
          this.router
            .navigate(['/AP', 'Computers.aspx'], {
              queryParams: { sidepanel: 'backup', hid: computer.hid, openEC2: 'true' }
            })
            .then(noop)
            .catch(noop);
        }
      });
  }

  private setParamsForOldWizardsToLocalStorage(computer: Computer, plan: PlanInfo): void {
    const lastRunInfo = plan?.lastRunInfo ? (plan.lastRunInfo.dateTimeUTC as string) : '';
    const params = {
      backupVersion: Computer.getAgent(computer, AgentType.Backup)?.version,
      email: computer.userAccount ? computer.userAccount.accountName : null,
      hid: computer.hid,
      id: plan?.id || this.wizardService.generateUUID(),
      isLinux: Computer.isWindows(computer) ? 'False' : 'True',
      lastStart: lastRunInfo ? moment.utc(lastRunInfo, baseDateFormatWithSeconds).format('M/D/YYYY+h:mm+A') || '' : '',
      mode: !plan?.id ? 'create' : 'edit',
      name: computer.name,
      profile: '',
      type: PlanTypes[plan.type],
      userId: computer.userAccount ? computer.userAccount.id : null
    };
    localStorage.setItem(plan.type === PlanTypes.DataBasePlan ? 'sqlWizardData' : 'EC2WizardData', JSON.stringify(params));
  }
}
